import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiCheckCircle, FiFileText, FiAward } from "react-icons/fi";
import { settingsAPI } from "../services/api";

const steps = [
  { num: "01", title: "Online Application", desc: "Fill out the online admission form with student and parent information. Upload required documents." },
  { num: "02", title: "Document Review", desc: "Our admissions team reviews your application and submitted documents within 3-5 business days." },
  { num: "03", title: "Entrance Test", desc: "Qualified applicants are invited for a written entrance test based on the grade applied for." },
  { num: "04", title: "Interview", desc: "Shortlisted students and parents are invited for an informal interview with the admissions panel." },
  { num: "05", title: "Admission Offer", desc: "Successful applicants receive an admission offer letter with fee details and enrollment instructions." },
  { num: "06", title: "Fee Payment & Enrollment", desc: "Pay the admission fee to confirm your seat and complete the enrollment process." },
];

const documents = [
  "Birth Certificate (original + photocopy)",
  "Previous school's Transfer Certificate (TC)",
  "Previous year's Mark Sheet/Report Card",
  "Passport-size photographs (4 copies)",
  "Citizenship certificate of parents/guardians",
  "Character Certificate from previous school",
  "Medical fitness certificate",
  "Immunization records",
];

const fallbackFees = [
  { level: "Primary (Grade 1-5)",       admission: "NPR 15,000", monthly: "NPR 3,500", annual: "NPR 8,000" },
  { level: "Secondary (Grade 6-10)",    admission: "NPR 20,000", monthly: "NPR 4,500", annual: "NPR 10,000" },
  { level: "High School (Grade 11-12)", admission: "NPR 25,000", monthly: "NPR 5,500", annual: "NPR 12,000" },
];

const scholarships = [
  { title: "Merit Scholarship",  desc: "Awarded to students scoring 90%+ in previous year's examination. Covers up to 50% of tuition fees." },
  { title: "Need-Based Aid",     desc: "Financial assistance for deserving students from economically disadvantaged backgrounds." },
  { title: "Sports Excellence",  desc: "For students representing school/district/national level in sports. Covers admission fee." },
  { title: "Sibling Discount",   desc: "10% discount on monthly tuition fees for siblings enrolled simultaneously." },
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const Admissions = () => {
  const [fees, setFees] = useState(fallbackFees);

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const data = res.data?.data?.settings?.feeStructure || [];
      if (data.length > 0) setFees(data);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Join Our Family</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Admissions <span className="text-sky-300">Open</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed mb-8">
              We welcome students who are eager to learn, grow, and contribute to our vibrant school community.
              Admissions are open for the 2025-2026 academic year.
            </p>
            <Link
              to="/admissions/apply"
              className="inline-flex items-center gap-3 bg-white text-[#1D4ED8] px-8 py-4 font-bold rounded-xl uppercase tracking-widest text-xs hover:bg-blue-50 transition-all shadow-md"
            >
              Apply Online Now
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Admission Process */}
      <section id="process" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="How to Apply" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Admission <span className="text-[#2563EB]">Process</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-blue-100 shadow-sm hover:shadow-lg hover:border-[#2563EB]/30 transition-all duration-300 relative group"
              >
                <div className="text-6xl font-black text-blue-50 group-hover:text-blue-100 absolute top-4 right-4 leading-none select-none transition-colors">
                  {step.num}
                </div>
                <div className="relative">
                  <div className="w-11 h-11 bg-[#2563EB] rounded-xl flex items-center justify-center text-white font-bold text-sm mb-6 shadow-sm group-hover:bg-[#1D4ED8] transition-colors">
                    {step.num}
                  </div>
                  <h3 className="font-bold text-[#0F172A] text-xl mb-3">{step.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-20 lg:py-28 bg-[#F0F4FF]">
        <div className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Be Prepared" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Required <span className="text-[#2563EB]">Documents</span></h2>
          </motion.div>

          <div className="bg-white rounded-2xl p-10 lg:p-12 border border-blue-100 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                <FiFileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] text-xl">Document Checklist</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
              {documents.map((doc, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.06 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <FiCheckCircle className="w-5 h-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-sm leading-relaxed">{doc}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-8 text-slate-400 text-xs border-t border-blue-50 pt-6">
              Note: All documents must be submitted in original with one set of photocopies. Documents not in English
              or Nepali must be accompanied by a certified translation.
            </p>
          </div>
        </div>
      </section>

      {/* Tuition Fees */}
      <section id="fees" className="py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Transparent Pricing" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Tuition &amp; Fee <span className="text-[#2563EB]">Structure</span></h2>
          </motion.div>

          <div className="overflow-x-auto rounded-2xl border border-blue-100 shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#1e3a8a] text-white">
                  <th className="px-6 py-5 font-bold text-xs uppercase tracking-widest rounded-tl-2xl">Level</th>
                  <th className="px-6 py-5 font-bold text-xs uppercase tracking-widest">Admission Fee</th>
                  <th className="px-6 py-5 font-bold text-xs uppercase tracking-widest">Monthly Fee</th>
                  <th className="px-6 py-5 font-bold text-xs uppercase tracking-widest rounded-tr-2xl">Annual Fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-50 bg-white">
                {fees.map((fee, idx) => (
                  <tr key={idx} className="hover:bg-[#F0F4FF] transition-colors">
                    <td className="px-6 py-5 font-bold text-[#0F172A] text-sm">{fee.level}</td>
                    <td className="px-6 py-5 text-slate-600 text-sm">{fee.admission}</td>
                    <td className="px-6 py-5 text-slate-600 text-sm">{fee.monthly}</td>
                    <td className="px-6 py-5 text-slate-600 text-sm">{fee.annual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-slate-400 text-xs">
            * Fees are subject to revision. Additional charges may apply for transportation, books, and uniforms.
          </p>
        </div>
      </section>

      {/* Scholarships */}
      <section id="scholarships" className="py-20 lg:py-28 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <span className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Financial Support</span>
            </div>
            <h2 className="text-4xl font-bold text-white">Scholarships &amp; <span className="text-sky-300">Aid</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            {scholarships.map((s, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 rounded-2xl p-8 flex gap-5 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#2563EB] transition-colors">
                  <FiAward className="w-6 h-6 text-sky-300 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg mb-2">{s.title}</h3>
                  <p className="text-blue-200/70 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-start">
            <Link
              to="/admissions/apply"
              className="inline-flex items-center gap-3 bg-white text-[#1D4ED8] px-8 py-4 font-bold rounded-xl uppercase tracking-widest text-xs hover:bg-blue-50 transition-all shadow-md"
            >
              Start Your Application
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
